/**
 * Content Hooks
 * 
 * These hooks replace the Directus hooks with hardcoded content from content.config.ts
 * They maintain the same API so existing components don't need to change.
 */

import {
  siteMetadata,
  layoutConfig,
  footerPages,
  modalConfig,
  featureFlags,
  accountContent,
  consultationResultContent,
  accountConsultationResultContent,
  accountGoalContent,
  pillComponentContent,
  benefitsComponentContent,
  facebookLandingContent,
  homeExpertsContent,
  partnersContent,
  reviewsContent,
} from '@src/config/content.config';

/**
 * Replace useLayoutData hook
 * Returns layout configuration and site metadata
 */
export const useLayoutData = () => {
  return {
    site: {
      siteMetadata,
    },
    directusSetting: layoutConfig,
    allDirectusGenericPage: {
      edges: footerPages.map(page => ({
        node: page,
      })),
    },
    allDirectusModal: {
      nodes: modalConfig.modals,
    },
    directusFeatureFlag: {
      flags: featureFlags,
    },
  };
};

/**
 * Replace useAccountText hook
 * Returns all account-related content
 */
export const useAccountText = () => {
  return {
    directusAccount: accountContent,
    directusConsultationResult: consultationResultContent,
    directusAccountGoal: accountGoalContent,
    directusAccountOrder: {
      title: 'Orders',
      text: 'View your order history',
      no_orders_subtitle: 'You have no orders yet',
      number_label: 'Order #',
      date_label: 'Date',
      total_label: 'Total',
      discount_label: 'Discount',
      shipping_label: 'Shipping',
    },
    directusAccountSubscription: {
      title: 'Subscriptions',
      has_subscriptions_subtitle: 'Manage your active subscriptions',
      no_subscriptions_subtitle: 'You have no active subscriptions',
      apply_the_coupon_text: 'Apply coupon code',
      latest_pack_label: 'Latest pack',
      delivered_by_label: 'Delivered by',
      invoice_label: 'Invoice',
      manage_button_label: 'Manage',
      update_button_label: 'Update',
      pause_button_label: 'Pause',
      unpause_button_label: 'Resume',
      cancel_button_label: 'Cancel',
      restart_now_button_label: 'Restart',
      save_unpause_button_label: 'Save',
      cancel_success_label: 'Subscription cancelled',
      pause_title: 'Pause Subscription',
      unpause_title: 'Resume Subscription',
      pause_description: 'Your subscription will be paused',
      unpause_description: 'Your subscription will resume',
      pause_restart_label: 'Restart date',
      pause_cancel_button_label: 'Cancel',
      pause_confirm_button_label: 'Confirm',
      pause_success_label: 'Subscription paused',
      modify_pause_success_label: 'Pause updated',
      update_delivery_title: 'Update Delivery',
      update_delivery_button_label: 'Update',
      change_payment_method_label: 'Change payment method',
      update_success_label: 'Updated successfully',
      cancelation_reason_title: 'Why are you cancelling?',
      cancelation_reasons: [
        { reason: 'Too expensive' },
        { reason: 'No longer needed' },
        { reason: 'Switching to another provider' },
        { reason: 'Other' },
      ],
      restart_success_label: 'Subscription restarted',
    },
    directusAccountReferral: {
      title: 'Refer a Friend',
      subtitle: 'Give £10, Get £10',
      description: 'Share Vitl with friends and you both get £10 off',
      refer_friend_title: 'Your referral link',
      no_referral_text: 'Referral program coming soon',
    },
    directusCheckout: {
      delivery_title: 'Delivery Address',
      delivery_first_name_label: 'First name',
      delivery_last_name_label: 'Last name',
      delivery_address_label: 'Address',
      delivery_city_label: 'City',
      delivery_postcode_label: 'Postcode',
      delivery_manual_button_label: 'Enter manually',
    },
    directusPillComponent: pillComponentContent,
    directusBenefitsComponent: benefitsComponentContent,
    directusAccountConsultationResult: accountConsultationResultContent,
    directusSetting: {
      read_label_text: layoutConfig.read_label_text,
    },
  };
};

/**
 * Replace useFacebookLandingText hook
 * Returns landing page content
 */
export const useFacebookLandingText = () => {
  return {
    landing: facebookLandingContent,
    home: homeExpertsContent,
  };
};

/**
 * Replace useKitText hook (if needed)
 * Returns kit registration page content
 */
export const useKitText = () => {
  return {
    directusRegisterYourKit: {
      page_title: 'Register Your Kit',
      page_description: 'Register your DNA or blood test kit',
      title: 'Register Your Test Kit',
      description: 'Enter your kit barcode to get started',
      find_barcode_title: 'Find your barcode',
      find_barcode_description: 'Located on the side of your kit',
      find_barcode_placeholder: 'Enter barcode',
      find_barcode_button_label: 'Continue',
      dna_kit_title: 'DNA Test Kit',
      dna_kit_icon: { title: 'DNA Icon', filename_disk: 'dna-icon.svg' },
      dna_kit_image: { title: 'DNA Kit', filename_disk: 'dna-kit.jpg' },
      blood_test_title: 'Blood Test Kit',
      blood_test_icon: { title: 'Blood Icon', filename_disk: 'blood-icon.svg' },
      blood_test_image: { title: 'Blood Kit', filename_disk: 'blood-kit.jpg' },
      barcode_icon: { filename_disk: 'barcode-icon.svg' },
      user_icon: { filename_disk: 'user-icon.svg' },
    },
    directusBloodConsent: {
      title: 'Blood Test Consent',
      content: 'By registering this kit, you consent to the blood test analysis...',
    },
    directusDnaConsent: {
      title: 'DNA Test Consent',
      content: 'By registering this kit, you consent to the DNA analysis...',
    },
  };
};

/**
 * Replace useLogo hook
 * Returns logo configuration
 */
export const useLogo = () => {
  return {
    directusSetting: {
      logo: layoutConfig.logo,
      logo_primary: layoutConfig.logo_primary,
    },
  };
};

/**
 * Replace usePartnersText hook
 * Returns partner logos for partners slider
 */
export const usePartnersText = () => {
  return partnersContent;
};

/**
 * Replace reviews content
 * Returns reviews data for reviews component
 */
export const useReviewsContent = () => {
  return {
    directusHome: reviewsContent,
  };
};
